/* global axios */
import ApiClient from '../ApiClient';

class ConversationApi extends ApiClient {
  constructor() {
    super('conversations', { accountScoped: true });
  }

  get({
    inboxId,
    status,
    assigneeType,
    page,
    labels,
    teamId,
    conversationType,
    sortBy,
    classification,
    read_status,
  }) {
    return axios.get(this.url, {
      params: {
        inbox_id: inboxId,
        team_id: teamId,
        status,
        assignee_type: assigneeType,
        page,
        labels,
        conversation_type: conversationType,
        sort_by: sortBy,
        classification,
        read_status,
      },
    });
  }

  filter(payload) {
    return axios.post(`${this.url}/filter`, payload.queryData, {
      params: {
        page: payload.page,
      },
    });
  }

  search({ q }) {
    return axios.get(`${this.url}/search`, {
      params: {
        q,
        page: 1,
      },
    });
  }

  toggleStatus({
    conversationId,
    status,
    snoozedUntil = null,
    validateStatus = {},
  }) {
    if (status != 'resolved') {
      const targetInboxId =
        validateStatus?.activeInbox ||
        validateStatus?.conversation?.inbox_id;

      const openContactConversations = validateStatus?.contactConversations?.filter(
        conversation =>
          conversation.status !== 'resolved' &&
          conversation.id !== conversationId
      );
      const sameInbox = openContactConversations?.filter(
        conversation => conversation.inbox_id == targetInboxId
      );
      if (sameInbox.length > 0) {
        bus.$emit('newToastMessage', validateStatus?.messageOpen);
        return;
      }
    }
    return axios
      .post(`${this.url}/${conversationId}/toggle_status`, {
        status,
        snoozed_until: snoozedUntil,
      })
      .then(response => {
        bus.$emit('newToastMessage', validateStatus?.messageSaved);
        return response;
      });
  }

  togglePriority({ conversationId, priority }) {
    return axios.post(`${this.url}/${conversationId}/toggle_priority`, {
      priority,
    });
  }

  assignAgent({ conversationId, agentId }) {
    return axios.post(
      `${this.url}/${conversationId}/assignments?assignee_id=${agentId}`,
      {}
    );
  }

  assignTeam({ conversationId, teamId }) {
    const params = { team_id: teamId };
    return axios.post(`${this.url}/${conversationId}/assignments`, params);
  }

  markMessageRead({ id }) {
    return axios.post(`${this.url}/${id}/update_last_seen`);
  }

  markMessagesUnread({ id }) {
    return axios.post(`${this.url}/${id}/unread`);
  }

  toggleTyping({ conversationId, status, isPrivate }) {
    return axios.post(`${this.url}/${conversationId}/toggle_typing_status`, {
      typing_status: status,
      is_private: isPrivate,
    });
  }

  mute(conversationId) {
    return axios.post(`${this.url}/${conversationId}/mute`);
  }

  unmute(conversationId) {
    return axios.post(`${this.url}/${conversationId}/unmute`);
  }

  meta({
    inboxId,
    status,
    assigneeType,
    labels,
    teamId,
    conversationType,
    classification,
    read_status,
  }) {
    return axios.get(`${this.url}/meta`, {
      params: {
        inbox_id: inboxId,
        status,
        assignee_type: assigneeType,
        labels,
        team_id: teamId,
        conversation_type: conversationType,
        classification,
        read_status,
      },
    });
  }

  queueStats({ inboxId } = {}) {
    return axios.get(`${this.url}/queue_stats`, {
      params: { inbox_id: inboxId },
    });
  }

  queueList({ inboxId } = {}) {
    return axios.get(`${this.url}/queue_list`, {
      params: { inbox_id: inboxId },
    });
  }

  sendEmailTranscript({ conversationId, email }) {
    return axios.post(`${this.url}/${conversationId}/transcript`, { email });
  }

  updateCustomAttributes({ conversationId, customAttributes }) {
    return axios.post(`${this.url}/${conversationId}/custom_attributes`, {
      custom_attributes: customAttributes,
    });
  }

  fetchParticipants(conversationId) {
    return axios.get(`${this.url}/${conversationId}/participants`);
  }

  updateParticipants({ conversationId, userIds }) {
    return axios.patch(`${this.url}/${conversationId}/participants`, {
      user_ids: userIds,
    });
  }

  getAllAttachments(conversationId) {
    return axios.get(`${this.url}/${conversationId}/attachments`);
  }

  getUnreadCount() {
    return axios.get(`${this.url}/unread_ids`);
  }
}

export default new ConversationApi();
